import React, { useEffect, useRef, useState } from "react";

const CANVAS_SIZE = 320;
const SCAN_INTERVAL = 220; // ms tra un frame e l'altro inviato al worker

const QrReader = ({ onScan, onError, onClose, facingMode = "environment" }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const canvasCtxRef = useRef(null);
  const streamRef = useRef(null);
  const workerRef = useRef(null);
  const rafRef = useRef(null);
  const lastScanRef = useRef(0);
  const hasResultRef = useRef(false);
  const closedRef = useRef(false);
  const stopInProgressRef = useRef(false);

  const onScanRef = useRef(onScan);
  const onErrorRef = useRef(onError);
  const onCloseRef = useRef(onClose);

  const [status, setStatus] = useState("Avvio fotocamera...");

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const notifyClosed = () => {
    if (closedRef.current) return;
    closedRef.current = true;
    onCloseRef.current && onCloseRef.current();
  };

  const stopScanner = async () => {
    if (stopInProgressRef.current) return;
    stopInProgressRef.current = true;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }

    const video = videoRef.current;
    try {
      if (video) {
        video.pause();
        if (video.srcObject) {
          const tracks = video.srcObject.getTracks();
          tracks.forEach((track) => track.stop());
          video.srcObject = null;
        }
      }
    } catch (err) {
      console.debug("Errore stop video", err);
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (_) {
          // ignora eventuali errori di stop
        }
      });
      streamRef.current = null;
    }

    stopInProgressRef.current = false;
  };

  const handleFatalError = (message) => {
    if (hasResultRef.current) return;
    setStatus(message);
    onErrorRef.current && onErrorRef.current(message);
    stopScanner().finally(() => notifyClosed());
  };

  const tick = (timestamp = 0) => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvasCtxRef.current;
    if (!video || !canvas || !ctx || !workerRef.current) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      if (timestamp - lastScanRef.current >= SCAN_INTERVAL && !hasResultRef.current) {
        lastScanRef.current = timestamp;
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        workerRef.current.postMessage(
          {
            buffer: imageData.data.buffer,
            width: imageData.width,
            height: imageData.height,
          },
          [imageData.data.buffer]
        );
      }
    }

    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    hasResultRef.current = false;
    closedRef.current = false;
    lastScanRef.current = 0;
    stopInProgressRef.current = false;
    setStatus("Avvio fotocamera...");

    const startScanner = async () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        handleFatalError("Fotocamera non disponibile su questo dispositivo.");
        return;
      }

      try {
        workerRef.current = new Worker(new URL("../workers/qrWorker.js", import.meta.url), { type: "module" });
        workerRef.current.onmessage = (event) => {
          const { text, error } = event.data || {};
          if (error) {
            handleFatalError("Errore lettura QR: " + error);
            return;
          }
          if (text && !hasResultRef.current) {
            hasResultRef.current = true;
            onScanRef.current && onScanRef.current(text);
            stopScanner().finally(() => notifyClosed());
          }
        };
        workerRef.current.onerror = (err) => {
          handleFatalError("Errore worker QR: " + (err?.message || "sconosciuto"));
        };

        canvasCtxRef.current = canvasRef.current?.getContext("2d", { willReadFrequently: true }) || null;

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode },
          audio: false,
        });
        streamRef.current = stream;

        const video = videoRef.current;
        if (!video) {
          handleFatalError("Video element non disponibile");
          return;
        }

        video.srcObject = stream;
        video.setAttribute("playsInline", "true");
        video.muted = true;
        await video.play();

        setStatus("Scanner attivo: avvicina il QR");
        rafRef.current = requestAnimationFrame(tick);
      } catch (err) {
        handleFatalError("Errore accesso fotocamera: " + (err?.message || "sconosciuto"));
      }
    };

    startScanner();

    return () => {
      stopScanner().finally(() => notifyClosed());
    };
  }, [facingMode]);

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative rounded-lg overflow-hidden border border-gray-200 shadow-inner">
        <video
          ref={videoRef}
          className="block w-80 h-80 object-cover bg-black"
          autoPlay
          playsInline
          muted
        />
        <div className="pointer-events-none absolute inset-0 border-2 border-white/60 rounded-lg" />
        <canvas ref={canvasRef} width={CANVAS_SIZE} height={CANVAS_SIZE} style={{ display: "none" }} />
      </div>
      <div className="text-xs text-gray-500 text-center" style={{ minHeight: "1.5rem" }}>{status}</div>
    </div>
  );
};

export default QrReader;
